import React from 'react';
import type { Account, Campaign } from '../../store/appStore';
import {
  PALMESUS_COLORS,
  PALMESUS_FONT,
  PALMESUS_HEADING_TRACKING,
  getPalmesusCampaign,
} from '../../lib/palmesusBrand';

interface Props {
  account: Account;
  campaign: Campaign;
  onFollow: () => void;
}

/**
 * Magenta full-bleed follow-gate.
 * Hvit (negativ) Palmesus-logo, bold Poppins heading med +10% letter-spacing.
 */
export const PalmesusFollowGate: React.FC<Props> = ({ account, campaign, onFollow }) => {
  const cleanHandle =
    (account.instagramUrl || 'palmesus')
      .replace('@', '')
      .replace(/^https?:\/\/(www\.)?instagram\.com\//, '')
      .replace(/\/$/, '')
      .trim() || 'palmesus';
  const meta = getPalmesusCampaign(campaign.slug);
  const subtitle = meta?.subtitle ?? 'Bystranda · 26–27 juni 2026';

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: PALMESUS_COLORS.pink, color: PALMESUS_COLORS.white }}
    >
      {/* Topp: logo (negativ, alltid stående, godt med luft). */}
      <header className="px-5 sm:px-10 pt-10 sm:pt-12 flex justify-center">
        <img
          src="/palmesus/p-logo-white.png"
          alt="Palmesus"
          style={{ width: 'clamp(110px, 28vw, 180px)', height: 'auto' }}
        />
      </header>

      <main className="flex-1 flex flex-col justify-center px-5 sm:px-10 py-10 sm:py-14">
        <p
          className="uppercase mb-4"
          style={{
            fontFamily: PALMESUS_FONT,
            fontWeight: 700,
            fontSize: '0.8125rem',
            letterSpacing: PALMESUS_HEADING_TRACKING,
            color: PALMESUS_COLORS.white,
            opacity: 0.95,
          }}
        >
          Skandinavias største strandfest
        </p>

        <h1
          style={{
            fontFamily: PALMESUS_FONT,
            fontWeight: 800,
            fontSize: 'clamp(2rem, 10vw, 6.5rem)',
            letterSpacing: PALMESUS_HEADING_TRACKING,
            lineHeight: 0.98,
            color: PALMESUS_COLORS.white,
            margin: 0,
            textTransform: 'uppercase',
            overflowWrap: 'break-word',
            hyphens: 'manual',
          }}
        >
          Send inn ditt sangønske
        </h1>

        <p
          style={{
            fontFamily: PALMESUS_FONT,
            fontWeight: 500,
            fontSize: 'clamp(1rem, 2.6vw, 1.25rem)',
            lineHeight: 1.55,
            color: PALMESUS_COLORS.white,
            opacity: 0.95,
            margin: '1.5rem 0 0 0',
            maxWidth: '36rem',
          }}
        >
          Følg <strong style={{ fontWeight: 700 }}>@{cleanHandle}</strong> på Instagram, kom tilbake hit, og send inn sangønsket ditt, så spiller vi den på Main Stage.
        </p>

        <p
          style={{
            fontFamily: PALMESUS_FONT,
            fontWeight: 500,
            fontSize: '0.875rem',
            color: PALMESUS_COLORS.white,
            opacity: 0.78,
            margin: '0.75rem 0 0 0',
          }}
        >
          {subtitle}
        </p>

        <div className="flex flex-col gap-3 mt-8" style={{ maxWidth: '26rem' }}>
          <button
            onClick={onFollow}
            style={{
              fontFamily: PALMESUS_FONT,
              fontWeight: 700,
              fontSize: '1rem',
              letterSpacing: PALMESUS_HEADING_TRACKING,
              textTransform: 'uppercase',
              padding: '1.05rem 1.75rem',
              backgroundColor: PALMESUS_COLORS.white,
              color: PALMESUS_COLORS.pink,
              border: 'none',
              cursor: 'pointer',
              transition: 'transform 200ms, background-color 200ms, color 200ms',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.backgroundColor = PALMESUS_COLORS.black;
              e.currentTarget.style.color = PALMESUS_COLORS.white;
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.backgroundColor = PALMESUS_COLORS.white;
              e.currentTarget.style.color = PALMESUS_COLORS.pink;
            }}
          >
            Følg @{cleanHandle}
          </button>
        </div>

        <div
          className="mt-8 px-5 py-4"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
            border: `1px solid rgba(255, 255, 255, 0.25)`,
            maxWidth: '32rem',
          }}
        >
          <p
            className="uppercase mb-2"
            style={{
              fontFamily: PALMESUS_FONT,
              fontWeight: 700,
              fontSize: '0.6875rem',
              letterSpacing: PALMESUS_HEADING_TRACKING,
              color: PALMESUS_COLORS.white,
              opacity: 0.9,
            }}
          >
            Slik fungerer det
          </p>
          <ol
            style={{
              color: PALMESUS_COLORS.white,
              fontSize: '0.9375rem',
              fontWeight: 500,
              lineHeight: 1.55,
              margin: 0,
              paddingLeft: '1.25rem',
              opacity: 0.95,
            }}
          >
            <li style={{ marginBottom: '0.4rem' }}>Trykk på knappen, så åpnes Instagram i ny fane.</li>
            <li style={{ marginBottom: '0.4rem' }}>Følg <strong style={{ fontWeight: 700 }}>@{cleanHandle}</strong>, og lukk Instagram-fanen.</li>
            <li>Du er allerede tilbake. Skriv inn sangønsket og send.</li>
          </ol>
        </div>
      </main>

      <footer
        className="px-5 sm:px-10 py-5"
        style={{
          fontFamily: PALMESUS_FONT,
          fontWeight: 700,
          fontSize: '0.6875rem',
          letterSpacing: PALMESUS_HEADING_TRACKING,
          color: PALMESUS_COLORS.white,
          opacity: 0.85,
          textTransform: 'uppercase',
          borderTop: '1px solid rgba(255, 255, 255, 0.18)',
          paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))',
        }}
      >
        Palmesus · Bystranda · Kristiansand
      </footer>
    </div>
  );
};
